/**
 * The 'editorial' theme — warm, refined magazine typesetting.
 *
 * Authority: `docs/architecture/studio-token-contract.md`. Reference: `xavior.ts`.
 *
 * DESIGN WORLD — "EDITORIAL"
 * A beautifully set long-form article. Where Xavior is a systematic product UI
 * and Boardroom is a formal corporate deck, Editorial is the printed magazine:
 * a contemporary old-style display serif (Fraunces, with optical character and
 * soft terminals) set over a warm reading serif body (Newsreader) — a true
 * serif-on-serif article, not a serif headline over a sans body. The canvas is
 * warm cream paper (not pure white), the accent is a muted terracotta/rust, and
 * a cool ink-teal carries the data so charts/stats read as a refined second
 * voice rather than a second brand color. Type breathes: a LOOSE 1.72 body
 * line-height, a generous reading measure, wide eyebrow tracking, and refined
 * hairline rules. It feels human, considered, and beautifully printed.
 *
 * STRUCTURALLY DISTINCT LAYOUT (§2.6 — the design-world knobs, all DISTINCT from
 * every other registered theme):
 *   - space-unit          7px   (between Mono 8 / Xavior 8 and Boardroom 12 —
 *                                 a finer, magazine-column rhythm)
 *   - gutter              72px  (Xavior 64, Boardroom 80, Keynote 88 — generous
 *                                 but not stage-sized; a wide article margin)
 *   - radius              14px  (largest of any theme — soft, paper-card feel)
 *   - radius-sm           7px   (half — for the marker-pill highlight)
 *   - line-height         1.72  (the LOOSEST body of any theme — long-form air)
 *   - line-height-display 1.06  (tight, optical-serif headline set)
 *   - tracking-display   -0.012em (subtle optical tightening for a serif at size)
 *   - tracking-eyebrow    0.26em  (the WIDEST eyebrow — a true magazine kicker)
 *   - measure             740px  (generous single-column reading width)
 *   - stat-size           clamp(3.25rem, 7.5vw, 5.5rem) (a stat set as a
 *                                 magazine pull-figure: large, never billboard)
 *
 * Renders well as: a deck slide (warm paper, terracotta accent square, big serif
 * stat), a doc section (loose serif prose at 740px measure with a marker
 * highlight + rust pull-quote), and a newsletter (the email renderer reads these
 * VALUES — cream bg, serif display, teal stats, terracotta links all carry).
 *
 * Pure data. No imports beyond the StudioTheme type.
 */
export const editorial = {
    id: 'editorial',
    label: 'Editorial',
    layoutLanguage: 'editorial',
    fontsUrl: 'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400;1,9..144,500&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400;1,6..72,500&display=swap',
    tokens: {
        // ── 2.1 typography ──
        // Display: Fraunces — a contemporary old-style magazine serif with optical
        // sizing and soft, characterful terminals. Body: Newsreader — a warm,
        // highly readable reading serif (serif-on-serif = the long-form article look).
        '--xv-art-font-display': "'Fraunces', 'Playfair Display', Georgia, 'Times New Roman', serif",
        '--xv-art-font-body': "'Newsreader', Georgia, 'Iowan Old Style', 'Times New Roman', serif",
        '--xv-art-font-mono': "'iA Writer Mono', 'SFMono-Regular', ui-monospace, Menlo, monospace",
        // Editorial display weight is a refined 500 (not heavy 700/800) — the brief's
        // "beautifully set, not bold". Body sits at 400 with loose air.
        '--xv-art-font-weight-display': '500',
        '--xv-art-font-weight-body': '400',
        // Subtle optical tightening for a serif at headline sizes; the widest eyebrow
        // tracking of any theme for a true magazine kicker.
        '--xv-art-tracking-display': '-0.012em',
        '--xv-art-tracking-eyebrow': '0.26em',
        // ── 2.2 surface ──
        // Warm cream paper, NOT pure white. A second, slightly deeper paper for
        // raised surfaces. The inverse is a warm near-black espresso, not blue-black.
        '--xv-art-bg': '#fbf7ef',
        '--xv-art-bg-surface': '#f4ecdd',
        '--xv-art-bg-inverse': '#241c14',
        '--xv-art-surface-hover': '#ece0cb',
        // ── 2.3 ink ──
        // Warm dark-brown ink (printed-on-cream, never #000). Secondary + muted step
        // down warmly. Inverse ink is the cream paper itself for the dark slide.
        '--xv-art-ink': '#2a211a',
        '--xv-art-ink-secondary': '#5b4f42',
        '--xv-art-ink-muted': '#9a8b76',
        '--xv-art-ink-inverse': '#f6efe2',
        // ── 2.4 accent ──
        // Muted terracotta / rust — warm, refined, magazine-pull color. accent-ink is
        // the cream paper (legible on a terracotta chip). accent-dim is the faint
        // wash behind a marker highlight. accent-2 is a cool ink-teal: the data /
        // chart second voice (richer palette than Mono/Xavior allow for charts).
        '--xv-art-accent': '#b0512f',
        '--xv-art-accent-ink': '#fbf7ef',
        '--xv-art-accent-dim': 'rgba(176, 81, 47, 0.12)',
        '--xv-art-accent-2': '#2f6f6a',
        // ── 2.5 borders ──
        // Refined warm hairlines — the magazine rule under a headline. The strong
        // variant is the deeper rule for table headers / headline underscores.
        '--xv-art-border': '#e6dac6',
        '--xv-art-border-strong': '#cdbb9f',
        // ── 2.6 layout — the structurally-distinct design world (all DISTINCT) ──
        '--xv-art-measure': '740px',
        '--xv-art-space-unit': '7px',
        '--xv-art-gutter': '72px',
        '--xv-art-radius': '14px',
        '--xv-art-radius-sm': '7px',
        '--xv-art-line-height': '1.72',
        '--xv-art-line-height-display': '1.06',
        // ── 2.7 stat primitives ──
        // Stats render in the cool ink-teal (a designed second voice on warm paper),
        // with warm-paper-appropriate up/down trend colors. Stat is a magazine
        // pull-figure: large but never a billboard.
        '--xv-art-stat-ink': '#2f6f6a',
        '--xv-art-stat-positive': '#3f7d4f',
        '--xv-art-stat-negative': '#b0512f',
        '--xv-art-stat-size': 'clamp(3.25rem, 7.5vw, 5.5rem)',
        // ── 3.1 highlight — marker-pill (the magazine highlighter) ──
        // Editorial highlight is the §3.1 marker style: terracotta-tinted background +
        // terracotta ink + small inline padding (a soft pill), medium weight.
        '--xv-art-highlight-ink': '#8f3f24',
        '--xv-art-highlight-bg': 'rgba(176, 81, 47, 0.14)',
        '--xv-art-highlight-weight': '500',
        '--xv-art-highlight-pad': '0.05em 0.28em',
        // ── 3.2 callout — warm paper panel, terracotta insight bar ──
        '--xv-art-callout-bg': '#f4ecdd',
        '--xv-art-callout-border': '#e6dac6',
        '--xv-art-callout-ink': '#3a2f25',
        '--xv-art-callout-radius': '14px',
        // Tracks the resolved accent so a brand kit's accent reaches the insight
        // bar / rules / links (#5319); renders byte-identical terracotta without a kit.
        '--xv-art-callout-accent-insight': 'var(--xv-art-accent, #b0512f)',
        '--xv-art-callout-accent-warning': '#bf8a2e',
        '--xv-art-callout-accent-success': '#3f7d4f',
        // ── 3.3 stat ──
        // Caption labels in warm muted ink with wide tracking; numerals stay in the
        // serif display (an editorial pull-figure is set in the headline face, not
        // tabular mono — that's a magazine convention, not a dashboard one).
        // Labels track the resolved muted ink (#5352).
        '--xv-art-stat-label-ink': 'var(--xv-art-ink-muted, #9a8b76)',
        '--xv-art-stat-label-tracking': '0.16em',
        '--xv-art-stat-numeral-font': "'Fraunces', 'Playfair Display', Georgia, serif",
        // ── 3.4 quote — oversized italic serif pull-quote with a terracotta rule ──
        '--xv-art-quote-ink': '#4a3d30',
        '--xv-art-quote-bar': 'var(--xv-art-accent, #b0512f)',
        '--xv-art-quote-font': "'Fraunces', 'Playfair Display', Georgia, serif",
        '--xv-art-quote-size': '1.7rem',
        '--xv-art-quote-style': 'italic',
        // ── 3.5 eyebrow — a true magazine kicker (widest tracking) ──
        // The kicker tracks the resolved muted ink (#5352). (The quote/callout/code/
        // wordmark inks and the teal stat-ink are DELIBERATE distinct editorial
        // voices, not ink copies — they stay literal, like highlight-ink in #5319.)
        '--xv-art-eyebrow-ink': 'var(--xv-art-ink-muted, #9a8b76)',
        '--xv-art-eyebrow-tracking': '0.26em',
        '--xv-art-eyebrow-size': '0.7rem',
        '--xv-art-eyebrow-transform': 'uppercase',
        '--xv-art-eyebrow-rule': 'var(--xv-art-accent, #b0512f)',
        // ── 3.6 wordmark — refined serif footer mark ──
        '--xv-art-wordmark-ink': '#8a7a64',
        '--xv-art-wordmark-font': "'Fraunces', 'Playfair Display', Georgia, serif",
        '--xv-art-wordmark-size': '0.95rem',
        '--xv-art-wordmark-weight': '500',
        // ── 3.7 optional treatments ──
        // Links in terracotta; inline/block code on a warm-paper surface in mono.
        '--xv-art-link-ink': 'var(--xv-art-accent, #b0512f)',
        '--xv-art-code-bg': '#f1e7d4',
        '--xv-art-code-ink': '#3a2f25',
        // ── 4.2 logo slot (theme leaves logo empty → serif text wordmark renders) ──
        '--xv-art-logo-url': 'none',
        '--xv-art-logo-aspect': 'auto',
        '--xv-art-logo-height': '0.95rem',
        '--xv-art-logo-invert': '0',
    },
    /**
     * Richer per-treatment geometry beyond what tokens express. The token-only
     * path above already produces a coherent result; this layer adds the magazine
     * flourishes (drop-cap-ready prose, a hanging eyebrow number rule, an
     * oversized hanging quotation mark, optical headline kerning). Scoped under
     * [data-xv-theme="editorial"] by the renderer.
     */
    layoutCss: `
[data-xv-theme="editorial"] {
  font-feature-settings: 'kern' 1, 'liga' 1, 'onum' 1, 'pnum' 1;
  text-rendering: optimizeLegibility;
}

/* Display type: optical sizing + ligatures; the serif wants its kerning. */
[data-xv-theme="editorial"] .xv-art-headline,
[data-xv-theme="editorial"] h1,
[data-xv-theme="editorial"] h2,
[data-xv-theme="editorial"] h3 {
  font-feature-settings: 'kern' 1, 'liga' 1, 'dlig' 1;
  font-optical-sizing: auto;
  text-wrap: balance;
}

/* Eyebrow: a magazine kicker with a short leading accent rule. */
[data-xv-theme="editorial"] .xv-art-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--xv-art-space-unit) * 1.5);
}
[data-xv-theme="editorial"] .xv-art-eyebrow::before {
  content: '';
  width: calc(var(--xv-art-space-unit) * 4);
  height: 1px;
  background: var(--xv-art-eyebrow-rule, var(--xv-art-accent));
  flex: 0 0 auto;
}

/* Pull-quote: an oversized hanging quotation mark, hairline left rule, italic. */
[data-xv-theme="editorial"] .xv-art-quote {
  position: relative;
  padding-left: calc(var(--xv-art-space-unit) * 5);
  border-left: 2px solid var(--xv-art-quote-bar, var(--xv-art-accent));
  line-height: 1.34;
}
[data-xv-theme="editorial"] .xv-art-quote::before {
  content: '\\201C';
  position: absolute;
  left: calc(var(--xv-art-space-unit) * 1.5);
  top: -0.12em;
  font-family: var(--xv-art-quote-font, var(--xv-art-font-display));
  font-size: 2.6em;
  line-height: 1;
  color: var(--xv-art-quote-bar, var(--xv-art-accent));
  opacity: 0.42;
  pointer-events: none;
}

/* Long-form prose: drop-cap-ready first paragraph; loose, airy reading set. */
[data-xv-theme="editorial"] .xv-art-prose,
[data-xv-theme="editorial"] .xv-art-summary {
  line-height: var(--xv-art-line-height, 1.72);
  font-feature-settings: 'kern' 1, 'liga' 1, 'onum' 1;
}
[data-xv-theme="editorial"] .xv-art-prose--lead::first-letter,
[data-xv-theme="editorial"] .xv-art-summary::first-letter {
  font-family: var(--xv-art-font-display);
  font-weight: var(--xv-art-font-weight-display, 500);
  float: left;
  font-size: 3.1em;
  line-height: 0.78;
  padding: 0.04em 0.1em 0 0;
  color: var(--xv-art-accent);
}

/* Marker highlight: a soft pill with the magazine highlighter feel. */
[data-xv-theme="editorial"] .xv-art-highlight {
  border-radius: var(--xv-art-radius-sm, 7px);
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

/* Hero stat: a magazine pull-figure — serif numerals, tight optical line. */
[data-xv-theme="editorial"] .xv-art-stat {
  font-feature-settings: 'kern' 1, 'onum' 0, 'lnum' 1, 'tnum' 0;
  line-height: 1;
}
[data-xv-theme="editorial"] .xv-art-stat__label {
  font-family: var(--xv-art-font-body);
}

/* Callout: a soft warm panel with a generous left insight bar. */
[data-xv-theme="editorial"] .xv-art-callout {
  border-radius: var(--xv-art-callout-radius, var(--xv-art-radius));
  padding: calc(var(--xv-art-space-unit) * 3) calc(var(--xv-art-space-unit) * 3.5);
}

/* Wordmark: a refined small-caps-feeling serif footer mark. */
[data-xv-theme="editorial"] .xv-art-wordmark {
  font-feature-settings: 'kern' 1, 'liga' 1;
  letter-spacing: 0.01em;
}
`.trim(),
};
//# sourceMappingURL=editorial.js.map